/**
 * Calibrators Page
 * Main page for calibrator CRUD operations
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { useCalibratorsList, useDeleteCalibrator } from '../hooks';
import { CalibratorTable } from '../components/CalibratorTable';
import { CalibratorModal } from '../components/CalibratorModal';
import type { Calibrator, CalibratorsFilters } from '../types';

// =============================================================================
// Page Component
// =============================================================================

export default function CalibratorsPage() {
  // ---------------------------------------------------------------------------
  // Filter state
  // ---------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<string>('all');
  const debouncedSearch = useDebounce(search, 300);

  // ---------------------------------------------------------------------------
  // Sort state
  // ---------------------------------------------------------------------------
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<number>(1);

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const pageLength = 25;

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, enabledFilter, sortKey, sortDirection]);

  // ---------------------------------------------------------------------------
  // Modal state
  // ---------------------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCalibrator, setEditingCalibrator] = useState<
    Calibrator | undefined
  >(undefined);

  // ---------------------------------------------------------------------------
  // Delete confirmation state
  // ---------------------------------------------------------------------------
  const [deleteTarget, setDeleteTarget] = useState<Calibrator | null>(null);
  const deleteMutation = useDeleteCalibrator();

  // ---------------------------------------------------------------------------
  // API query
  // ---------------------------------------------------------------------------
  const filters: CalibratorsFilters = {
    search: debouncedSearch || undefined,
    enabled: enabledFilter !== 'all' ? enabledFilter : undefined,
    page,
    pageLength,
    sortKey,
    sortDirection,
  };

  const calibratorsQuery = useCalibratorsList(filters);
  const calibrators = calibratorsQuery.data?.calibrators || [];
  const total = calibratorsQuery.data?.total || 0;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleEdit = (calibrator: Calibrator) => {
    setEditingCalibrator(calibrator);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingCalibrator(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.mongoCalibratorId);
    setDeleteTarget(null);
  };

  const handleSortChange = (key: string, direction: number) => {
    setSortKey(key);
    setSortDirection(direction);
  };

  const handleRefresh = () => {
    calibratorsQuery.refetch();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Calibrators{' '}
            <small>Calibration equipment management</small>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={calibratorsQuery.isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${calibratorsQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Calibrator
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search calibrators..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={enabledFilter} onValueChange={setEnabledFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Enabled</SelectItem>
            <SelectItem value="false">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {calibratorsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading calibrators. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <CalibratorTable
          calibrators={calibrators}
          total={total}
          page={page}
          pageLength={pageLength}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onPageChange={setPage}
          onSortChange={handleSortChange}
          onEdit={handleEdit}
          onDelete={setDeleteTarget}
          isLoading={calibratorsQuery.isLoading}
        />
      )}

      {/* Create/Edit Modal */}
      <CalibratorModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setEditingCalibrator(undefined);
        }}
        calibrator={editingCalibrator}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Calibrator</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
